// Shared types for the guided onboarding flow.

// Which track the user picked on the first screen.
export type OnboardingTrack = "business" | "startup";

// Resumable UI step keys — the *screens* the user clicks through. Persisted on the company
// (POST /onboarding/progress) so the user can leave and resume where they left off. These
// MUST stay in sync with the backend ONBOARDING_UI_STEP_* constants (and their order with
// ONBOARDING_UI_STEP_ORDER) in app/constants.py. They are distinct from OnboardingStepKey
// below, which tracks what the AI has *generated*, not which screen the user has seen.
//
// The order here defines resume order: on resume we jump to the first step the user has not
// completed. Append new steps to the END so existing users see them as not-yet-completed.
export type GuidedStepKey =
  | "track"
  | "intake"
  | "personalizing"
  | "icp"
  | "list"
  | "sequence"
  | "content"
  | "samples"
  | "agents"
  | "widgets"
  | "recap";

// MUST be in true live-flow order: resume jumps to the step after the furthest one completed,
// so an out-of-order entry breaks resume/finish detection. `list` therefore sits in its real
// flow position (icp -> list -> sequence), matching the Step enum in GuidedOnboarding.tsx and
// the backend ONBOARDING_UI_STEP_ORDER. A legacy user who never recorded `list` just has it
// absent from their set; the max-index resume math skips past it cleanly.
export const GUIDED_STEP_ORDER: GuidedStepKey[] = [
  "track",
  "intake",
  "personalizing",
  "icp",
  "list",
  "sequence",
  "content",
  "samples",
  "agents",
  "widgets",
  "recap",
];

// The resumable-progress payload from GET /onboarding/progress and the `progress` field of
// the personalize status poll. `resumeStep` is the first step the user hasn't completed
// (null once the whole flow is finished).
export interface OnboardingProgress {
  completedSteps: GuidedStepKey[];
  lastStep: GuidedStepKey | null;
  resumeStep: GuidedStepKey | null;
  completedAt: string | null;
}

// Personalization step keys — must match the backend onboarding_service step names.
// Phase 1 (gated on user confirmation): analyze, details.
// Phase 2: profile, sequence, content, agents.
export type OnboardingStepKey =
  | "analyze"
  | "details"
  | "profile"
  | "list"
  | "sequence"
  | "content"
  | "samples"
  | "agents"
  | "widgets";

// Intake answers collected on the "tell us about your business" screen.
// Fields differ slightly by track but we keep one shape for simplicity.
export interface GuidedIntake {
  // Common
  fullName: string;
  // Business track — description is derived from the website, not typed by the user.
  companyName: string;
  website: string;
  // Startup track
  businessName: string;
  whatToStart: string;
  idealCustomers: string;
}

// Status values mirrored from the backend onboarding service.
export type OnboardingStepStatus =
  | "pending"
  | "generating"
  | "complete"
  | "error";

export interface OnboardingStepState {
  status: OnboardingStepStatus;
  error?: string;
  summary?: string;
}

export interface IcpPersona {
  title?: string;
  summary?: string;
}

export interface IcpResult {
  id?: string;
  name?: string;
  industry?: string;
  companySize?: string;
  description?: string;
  personas?: IcpPersona[];
  challenges?: string[];
}

export interface ContentPillar {
  title?: string;
  description?: string;
  topics?: string[];
  stage?: string;
}

export interface ContentResult {
  name?: string;
  salesNarrative?: string;
  targetAudience?: string;
  pillars?: ContentPillar[];
}

export interface AgentResult {
  name?: string;
  title?: string;
  description?: string;
  picture?: string;
}

export interface SequenceStepSummary {
  stepName?: string;
  actionType?: string;
  daysAfterPrevious?: number;
  aiPersonalized?: boolean;
}

export interface SequenceResult {
  id?: string;
  name?: string;
  steps?: SequenceStepSummary[];
  // How many leads from the starter lead list were enrolled into this sequence during
  // onboarding (0 when there was no list or enrollment was skipped).
  enrolledLeadCount?: number;
}

export interface LeadListSample {
  name?: string;
  title?: string;
  company?: string;
}

export interface LeadListTargeting {
  segments?: string[];
  seniorities?: string[];
  city?: string;
  // Free-text business-keyword phrases the agent matched on (maps to the audience builder's
  // "Business Keywords" field, i.e. businessProfile.companyDescription).
  businessKeywords?: string[];
}

export interface LeadListResult {
  id?: string;
  name?: string;
  leadCount?: number;
  sampleLeads?: LeadListSample[];
  targeting?: LeadListTargeting;
}

// One generated sample content piece + its brand image, rendered styled like its channel.
export interface SampleContentPiece {
  channel?: "blog_post" | "linkedin_post" | "instagram_post";
  stage?: string;
  title?: string;
  body?: string;
  metaDescription?: string;
  imageUrl?: string;
  aspectRatio?: string;
  // Id of the saved Content row this preview was persisted as, so the user can open it to edit
  // and publish from the Content page after onboarding.
  contentId?: string;
}

export interface SampleContentResult {
  brand?: {
    companyName?: string;
    logoUrl?: string;
  };
  pieces?: SampleContentPiece[];
}

// A seeded team member configured as a website chat agent, shown on the "deploy on your website"
// step with its embed code (built client-side from agentId).
export interface WebsiteWidgetAgent {
  agentId?: string;
  role?: "sales" | "support";
  name?: string;
  title?: string;
  picture?: string;
  buttonText?: string;
}

export interface WebsiteWidgetResult {
  agents?: WebsiteWidgetAgent[];
}

export interface PersonalizationResults {
  icp?: IcpResult;
  list?: LeadListResult;
  sequence?: SequenceResult;
  content?: ContentResult;
  samples?: SampleContentResult;
  agents?: AgentResult[];
  widgets?: WebsiteWidgetResult;
}

// Editable company fields surfaced on the personalizing screen. Mirrors the `company`
// snapshot the backend returns from GET /onboarding/personalize/status, which the
// enrichment step live-fills (description/industry/type/size/location) while the user
// waits. `industry` and `size` are pick-list values (see INDUSTRIES / COMPANY_SIZES).
export interface CompanyDetails {
  id?: string;
  name?: string;
  website?: string;
  description?: string;
  industry?: string;
  type?: string;
  size?: string;
  location?: string;
  // Logo the analyze step pulled from the site (re-hosted on our CDN). The user can replace
  // it on the personalizing screen if we picked the wrong one.
  logoUrl?: string;
}

// The full polled status payload from GET /onboarding/personalize/status.
// `awaiting_confirmation` = Phase 1 done; waiting for the user to confirm company details
// before Phase 2 (ICP/content/agents) runs. `phase` is 1 during Phase 1, 2 afterward.
export interface PersonalizationState {
  status:
    | "generating"
    | "awaiting_confirmation"
    | "complete"
    | "error"
    | "not_started"
    | "not_found";
  phase?: number;
  track?: OnboardingTrack;
  steps?: Record<string, OnboardingStepState>;
  stepOrder?: string[];
  phase1Steps?: string[];
  results?: PersonalizationResults;
  company?: CompanyDetails;
  hadErrors?: boolean;
  error?: string | null;
  // Resumable UI-step progress, included on the status poll so a single round-trip can
  // both repopulate the previews and tell us where to resume.
  progress?: OnboardingProgress;
}
